import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';

import {
  useErpSurveyDigestListQuery,
  useErpSurveyGroupHistoryQuery,
  useErpSurveyGroupStudentsQuery,
  useRequestSurveyDigestMutation,
} from 'core/graphql';

import { usePageVisiblePoll } from '../Meta/usePageVisiblePoll';
import {
  IGroupStudentSurveyRow,
  ISurveyDigest,
  ISurveyGroupTimeline,
  ISurveyTimelineRow,
  groupStudentRowFromApi,
  surveyDigestActive,
  surveyDigestRetryAfterMs,
  surveyDigestStale,
  surveyDigestStaleAt,
  surveyErrorCode,
  SurveyDigestScope,
} from './insightsModel';

/**
 * Spec 0098 — the reads and the one write of «Обзор» опросов.
 */

const EMPTY_TIMELINE: ISurveyGroupTimeline = { rows: [], metrics: [] };

export const useSurveyGroupTimeline = (
  groupId?: string | null,
): {
  timeline: ISurveyGroupTimeline;
  loading: boolean;
  error?: ApolloError;
  /** The backend answered null — no such group. */
  missing: boolean;
  refetch: () => void;
} => {
  const { data, loading, error, refetch } = useErpSurveyGroupHistoryQuery({
    variables: { groupId: groupId as string },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
  });
  const timeline = useMemo<ISurveyGroupTimeline>(
    () =>
      data?.surveyGroupTimeline
        ? {
            rows: data.surveyGroupTimeline.rows as ISurveyTimelineRow[],
            metrics: data.surveyGroupTimeline.metrics,
          }
        : EMPTY_TIMELINE,
    [data],
  );
  return {
    timeline,
    loading,
    error,
    missing: Boolean(data) && !data?.surveyGroupTimeline,
    refetch: useCallback(() => {
      refetch().catch(() => undefined);
    }, [refetch]),
  };
};

/** How often a queued/running digest is re-read while the tab is visible. */
export const SURVEY_DIGEST_POLL_MS = 4000;

export const useSurveyDigests = (
  scope: SurveyDigestScope,
  scopeId?: string | null,
  { skip = false }: { skip?: boolean } = {},
): {
  digests: ISurveyDigest[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
  /**
   * Перепроверка F26: новейшая сводка зависла (бэкенд закрыл её с `stale:`
   * или она «в работе» дольше SURVEY_DIGEST_STALE_MS). Не опрашивается и не
   * держит «Пересобрать».
   */
  stale: boolean;
} => {
  const off = skip || !scopeId;
  const result = useErpSurveyDigestListQuery({
    variables: { scope, scopeId: scopeId as string, limit: 5 },
    skip: off,
    fetchPolicy: 'cache-and-network',
  });
  const digests = useMemo(
    () => (result.data?.surveyDigests ?? []) as ISurveyDigest[],
    [result.data],
  );
  // Poll only while the newest attempt is still in the works, and only in a
  // visible tab — a forgotten background tab must not hammer the API.
  //
  // Перепроверка F26: и только пока она не зависла. Воркер умер — строка
  // навсегда `queued`/`running`, и опрос каждые 4 с шёл бесконечно. Таймер
  // перерисовывает экран ровно на границе, чтобы опрос встал и без ответа
  // сервера (сервер теперь и сам закрывает такие строки при чтении).
  const latest = digests[0];
  const staleAt = surveyDigestStaleAt(latest);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (staleAt === null) return undefined;
    const wait = staleAt - Date.now();
    if (wait <= 0) return undefined;
    const timer = window.setTimeout(() => setTick((n) => n + 1), wait + 50);
    return () => window.clearTimeout(timer);
  }, [staleAt]);
  // `tick` — только повод перерисоваться; время берётся живое.
  const stale = tick >= 0 && surveyDigestStale(latest, Date.now());
  const active = surveyDigestActive(latest?.status) && !stale;
  usePageVisiblePoll(
    result.startPolling,
    result.stopPolling,
    SURVEY_DIGEST_POLL_MS,
    !off && active,
  );
  const { refetch } = result;
  return {
    digests,
    loading: result.loading,
    error: result.error,
    refetch: useCallback(() => {
      if (!off) refetch();
    }, [off, refetch]),
    stale,
  };
};

/**
 * «Сгенерировать сводку». Returns the refusal code (`digest_cooldown`…)
 * instead of throwing: the screen explains it next to the button.
 */
export const useGenerateSurveyDigest = (
  onQueued?: () => void,
): {
  generate: (
    scope: SurveyDigestScope,
    scopeId: string,
  ) => Promise<{
    ok: boolean;
    code: string | null;
    /** `digest_cooldown`: через сколько мс можно снова (из «retry in N min»). */
    retryAfterMs?: number;
  }>;
  pending: boolean;
} => {
  const [mutate, { loading }] = useRequestSurveyDigestMutation();
  const generate = useCallback(
    async (scope: SurveyDigestScope, scopeId: string) => {
      try {
        await mutate({ variables: { scope, scopeId } });
        if (onQueued) onQueued();
        return { ok: true, code: null };
      } catch (error) {
        const code =
          error instanceof ApolloError
            ? surveyErrorCode(error) ?? 'generic'
            : 'generic';
        return {
          ok: false,
          code,
          retryAfterMs:
            code === 'digest_cooldown' && error instanceof ApolloError
              ? surveyDigestRetryAfterMs(error.graphQLErrors?.[0]?.message)
              : undefined,
        };
      }
    },
    [mutate, onQueued],
  );
  return { generate, pending: loading };
};

interface IGroupStudentsState {
  rows: IGroupStudentSurveyRow[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

const EMPTY_STUDENTS: IGroupStudentSurveyRow[] = [];

/**
 * The group's students × their survey answers — one server-side rollup
 * (`surveyGroupStudents`): who could answer and who did (silent ones
 * included), the mean index, negative responses and the newest comment.
 */
export const useGroupStudentSurveyStats = (
  groupId: string | null | undefined,
  { skip = false }: { skip?: boolean } = {},
): IGroupStudentsState => {
  const { data, loading, error, refetch } = useErpSurveyGroupStudentsQuery({
    variables: { groupId: groupId as string },
    skip: skip || !groupId,
    fetchPolicy: 'cache-and-network',
  });
  const rows = useMemo(
    () =>
      data?.surveyGroupStudents
        ? data.surveyGroupStudents.map(groupStudentRowFromApi)
        : EMPTY_STUDENTS,
    [data],
  );
  return {
    rows,
    loading,
    error,
    refetch: useCallback(() => {
      refetch().catch(() => undefined);
    }, [refetch]),
  };
};
